import { useCallback, useEffect, useMemo, useState } from "react";
import { CONFIG } from "./config.js";
import { fmtDate, monthLabel, isoAddDays } from "./format.ts";
import { todayISO } from "./dates.ts";
import {
  filterRows, totals, rates, leaderboard, monthlyKpi, positionSnapshots, rtoBenchmark,
} from "./metrics.ts";
import { toast } from "./toast.ts";
import { KpiCards } from "./components/KpiCards.tsx";
import { TopPerformer } from "./components/TopPerformer.tsx";
import { Leaderboard } from "./components/Leaderboard.tsx";
import { Outcomes } from "./components/Outcomes.tsx";
import { PositionStats, Positions } from "./components/Positions.tsx";
import { KpiLegend, KpiBenchmark } from "./components/KpiBenchmark.tsx";
import { RtoBenchmark } from "./components/RtoBenchmark.tsx";
import type { RtoPhase } from "./components/RtoBenchmark.tsx";
import { LogDailyModal } from "./components/LogDailyModal.tsx";
import { LogRtoModal } from "./components/LogRtoModal.tsx";
import { LogKpiModal } from "./components/LogKpiModal.tsx";
import { AdminPanel } from "./components/AdminPanel.tsx";
import { RtoDirectoryModal } from "./components/RtoDirectoryModal.tsx";
import type { Row, Achievement, RtoItem, RtoStatus, EntryApi } from "./domain.ts";

// App sở hữu TOÀN BỘ state của dashboard. app.js chỉ còn bootstrap: quyết
// demo/live, lo MSAL + màn landing, nạp data lần đầu rồi mount component này.
//
// Nhờ vậy renderAll() và cả cụm renderX() biến mất: đổi filter là setState,
// React tự vẽ lại đúng những section phụ thuộc.

export type RtoApi = {
  fetch: () => Promise<RtoItem[]>;
  updateStatus: (id: number, status: RtoStatus) => Promise<void>;
  loadActiveStacks: () => Promise<string[]>;
  saveActiveStacks: (stacks: string[]) => Promise<void>;
};

export type AppProps = {
  initialRows: Row[];
  initialAchievements: Achievement[];
  /** Nạp lại data từ nguồn (SharePoint hoặc demo in-memory). */
  reload: () => Promise<{ rows: Row[]; achievements: Achievement[] }>;
  rto: RtoApi;
  entry: {
    isDemo: boolean;
    account: { name: string; username: string } | null;
    api: EntryApi;
  };
};

type Range = "all" | "30" | "7" | "custom";

const PILLS: { range: Range; label: string }[] = [
  { range: "all", label: "All Time" },
  { range: "30", label: "30 Days" },
  { range: "7", label: "7 Days" },
];

/** Label khoảng thời gian cho card Top Performer. */
function scopeLabel(range: Range, shown: { from: string; to: string }): string {
  if (range === "all") return "all-time";
  return `${fmtDate(shown.from)} → ${fmtDate(shown.to)}`;
}

// Logo Eastgate — markup copy nguyên từ index.html.
function EastgateMark() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 57 35" fill="none">
      <path d="M56.8154 0V35H29.4355V26.25H48.3164V7.9541H37.8486V13.125H43.4854V21.4775H29.4355V0H56.8154Z" fill="#FFFFFF" />
      <path d="M27.3799 0V21.4775H13.2871V13.125H18.9238V7.9541H8.45508V26.25H27.3799V35H0V0H27.3799Z" fill="#FFFFFF" />
    </svg>
  );
}

/** Modal nào đang mở — mỗi lúc chỉ một. */
type OpenModal = "daily" | "rto" | "kpi" | "rto-directory" | null;

export function App({
  initialRows, initialAchievements, reload, rto, entry,
}: AppProps) {
  const [rows, setRows] = useState<Row[]>(initialRows);
  const [achievements, setAchievements] = useState<Achievement[]>(initialAchievements);

  const [range, setRange] = useState<Range>("all");
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [recruiter, setRecruiter] = useState("");
  const [kpiMonth, setKpiMonth] = useState<string | null>(null);

  const [activeStacks, setActiveStacks] = useState<string[]>([]);
  const [rtoItems, setRtoItems] = useState<RtoItem[] | null>(null);
  const [rtoError, setRtoError] = useState<string | null>(null);

  const [modal, setModal] = useState<OpenModal>(null);
  const [adminOpen, setAdminOpen] = useState(false);

  // ---- derived ----

  const maxWeek = useMemo(
    () => rows.reduce((m, r) => (r.weekEnding > m ? r.weekEnding : m), "0000-00-00"), [rows]);
  const minWeek = useMemo(
    () => rows.reduce((m, r) => (r.weekEnding < m ? r.weekEnding : m), "9999-99-99"), [rows]);

  // Preset 7/30 days tính lùi từ ngày log mới nhất (bao gồm ngày đó);
  // "all" = không lọc; "custom" = 2 ô date của user.
  const effective = useMemo(() => {
    if (!rows.length) return { from: null as string | null, to: null as string | null };
    if (range === "custom") return { from: fromDate, to: toDate };
    if (range === "all") return { from: null, to: null };
    return { from: isoAddDays(maxWeek, -(Number(range) - 1)), to: maxWeek };
  }, [rows.length, range, fromDate, toDate, maxWeek]);

  // Khoảng HIỂN THỊ trên 2 ô date: fallback về min/max của data để user luôn
  // nhìn thấy report đang chạy từ ngày nào đến ngày nào.
  const shown = {
    from: effective.from || (rows.length ? minWeek : ""),
    to: effective.to || (rows.length ? maxWeek : ""),
  };

  const activeRows = useMemo(() => filterRows(rows, {
    recruiter: recruiter || undefined,
    fromDate: effective.from || undefined,
    toDate: effective.to || undefined,
  }), [rows, recruiter, effective]);

  const t = useMemo(() => totals(activeRows), [activeRows]);
  const lb = useMemo(() => leaderboard(activeRows), [activeRows]);

  const interviewTarget = CONFIG.interviewWeeklyTarget || 5;
  // now = hôm nay theo lịch máy. Badge "NOT UPDATED THIS WEEK" so với tuần
  // hiện tại, nên cả team ngừng log là mọi vị trí đều bị cảnh báo.
  const today = todayISO();
  const snaps = useMemo(
    () => positionSnapshots(activeRows, { interviewTarget, now: today }),
    [activeRows, interviewTarget, today]);

  const recruiterNames = useMemo(
    () => [...new Set(rows.map((r) => r.recruiter))].sort(), [rows]);

  const months = useMemo(() => [...new Set([
    ...rows.map((r) => r.weekEnding.slice(0, 7)),
    ...achievements.map((a) => a.month),
  ])].sort(), [rows, achievements]);

  // Tháng đang xem: giữ lựa chọn của user nếu còn hợp lệ, ngược lại tháng mới nhất.
  const month = kpiMonth && months.includes(kpiMonth) ? kpiMonth : (months.at(-1) ?? null);

  const kpiRows = useMemo(() => {
    if (!month) return [];
    // RTO có RTODate → đếm theo tháng được cho MỌI tháng (monthlyKpi tự lọc).
    const items = rtoItems
      ? rtoItems.filter((i) => !(CONFIG.excludeRecruiters || []).includes(i.recruiter))
      : null;
    const list = monthlyKpi(rows, achievements, month, { rtoItems: items });
    return recruiter ? list.filter((x) => x.recruiter === recruiter) : list;
  }, [rows, achievements, month, rtoItems, recruiter]);

  const { rtoTargetMin: rtoMin, rtoTargetMax: rtoMax } = CONFIG;

  const rtoPhase = useMemo((): RtoPhase => {
    if (rtoError) return { kind: "error", message: rtoError };
    if (!rtoItems) return { kind: "loading" };
    if (!activeStacks.length) return { kind: "no-stacks" };
    const ready = rtoItems.filter((i) => i.status === "Ready");
    const bench = rtoBenchmark(ready, { activeStacks, min: rtoMin });
    const withTarget = bench.filter((b) => !b.noTarget);
    return {
      kind: "ready",
      bench,
      totalRto: bench.reduce((s, b) => s + b.rto, 0),
      behind: withTarget.filter((b) => b.gap > 0).length,
      withTarget: withTarget.length,
    };
  }, [rtoItems, rtoError, activeStacks, rtoMin]);

  // ---- actions ----

  const refreshData = useCallback(async () => {
    try {
      const d = await reload();
      setRows(d.rows);
      setAchievements(d.achievements);
    } catch {
      // Bootstrap (app.js) đã hiện lỗi trên màn landing — ở đây chỉ cần không
      // để rejection nổi lên thành unhandled, và giữ nguyên data đang có.
    }
  }, [reload]);

  const refreshRto = useCallback(async () => {
    setRtoError(null);
    try {
      setRtoItems(await rto.fetch());
    } catch (e) {
      setRtoError(e instanceof Error ? e.message : String(e));
    }
  }, [rto]);

  const changeRtoStatus = useCallback(async (id: number, status: RtoStatus) => {
    const cand = (rtoItems || []).find((i) => i.id === id);
    // Không esc() trong confirm(): đó là plain text.
    if (!confirm(`${cand?.name || ""} → ${status}? Ứng viên sẽ rời khỏi benchmark chờ offer.`)) return;
    try {
      await rto.updateStatus(id, status);
      toast(`✓ ${cand?.name || ""} → ${status}.`);
      await refreshRto();
      if (status === "Hired") setModal("kpi"); // hire xong → log luôn achievement
    } catch (e) {
      toast(`Không cập nhật được: ${e instanceof Error ? e.message : String(e)}`, false);
    }
  }, [rtoItems, rto, refreshRto]);

  const stepMonth = (dir: number) => {
    const i = months.indexOf(month ?? "") + dir;
    const next = months[i];
    if (next) setKpiMonth(next);
  };

  const onDateChange = (which: "from" | "to", value: string) => {
    let from = which === "from" ? (value || null) : shown.from || null;
    let to = which === "to" ? (value || null) : shown.to || null;
    if (from && to && from > to) [from, to] = [to, from];
    setRange("custom");
    setFromDate(from);
    setToDate(to);
  };

  // ---- effects ----

  // Nạp RTO + active stacks 1 lần sau khi mount.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const stacks = await rto.loadActiveStacks().catch(() => [] as string[]);
      if (alive) setActiveStacks(stacks);
      try {
        const items = await rto.fetch();
        if (alive) setRtoItems(items);
      } catch (e) {
        if (alive) setRtoError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { alive = false; };
  }, [rto]);

  const saveStacks = useCallback(async (stacks: string[]) => {
    await rto.saveActiveStacks(stacks);
    setActiveStacks(stacks);
  }, [rto]);

  const isAdmin = entry.isDemo ||
    !!(entry.account && (CONFIG.admins || []).includes(entry.account.username));

  return (
    <main id="dashboard">
      <header className="topbar">
        <div className="brand">
          <div className="brand-badge"><EastgateMark /></div>
          <div>
            <h1>TA PERFORMANCE <span>STATS</span></h1>
            <p className="brand-sub">
              CURRENT TIME REFERENCE: <span id="time-ref">{rows.length ? fmtDate(maxWeek) : "—"}</span>
            </p>
          </div>
        </div>
        <div className="controls">
          <div className="pill-group" id="time-filter">
            {PILLS.map((p) => (
              <button
                key={p.range}
                className={range === p.range ? "pill active" : "pill"}
                data-range={p.range}
                onClick={() => setRange(p.range)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="date-range" title="Khoảng ngày đang áp dụng — sửa để lọc tùy ý">
            <input type="date" id="date-from" value={shown.from}
              onChange={(e) => onDateChange("from", e.target.value)} />
            <span className="arrow">→</span>
            <input type="date" id="date-to" value={shown.to}
              onChange={(e) => onDateChange("to", e.target.value)} />
          </div>
          <select id="recruiter-filter" className="select" value={recruiter}
            onChange={(e) => setRecruiter(e.target.value)}>
            <option value="">All Recruiters</option>
            {recruiterNames.map((n) => <option value={n} key={n}>{n}</option>)}
          </select>
          <button id="btn-refresh" className="btn-icon" title="Refresh data"
            onClick={() => void refreshData()}>⟳</button>
          <button id="btn-log-weekly" className="btn-primary" onClick={() => setModal("daily")}>＋ Log Daily</button>
          <button id="btn-log-rto" className="btn-primary outline" onClick={() => setModal("rto")}>＋ Log RTO</button>
          <button id="btn-log-kpi" className="btn-primary outline" onClick={() => setModal("kpi")}>＋ Log KPI</button>
          {isAdmin && (
            <button id="btn-admin" className="btn-primary dark"
              onClick={() => setAdminOpen((v) => !v)}>Admin</button>
          )}
        </div>
      </header>

      {adminOpen && (
        <AdminPanel
          api={entry.api} isDemo={entry.isDemo}
          activeStacks={activeStacks}
          onSaveStacks={saveStacks}
          onDataChanged={refreshData}
          onClose={() => setAdminOpen(false)}
        />
      )}

      <section className="kpi-cards" id="kpi-cards">
        <KpiCards totals={t} rates={rates(t)} />
      </section>

      <div className="grid">
        <div className="col-left">
          <section className="card top-performer" id="top-performer">
            <TopPerformer leaderboard={lb} scope={scopeLabel(range, shown)} />
          </section>
          <section className="card">
            <div className="card-head">
              <h2>Outcome Distribution</h2>
              <span className="card-sub">CURRENT FILTERS</span>
            </div>
            <div id="outcome-bars"><Outcomes totals={t} /></div>
          </section>
        </div>
        <div className="col-right">
          <section className="card">
            <div className="card-head">
              <h2>🏆 TA Team Leaderboard</h2>
              <span className="card-sub">SUCCESS RANK UNDER ACTIVE FILTERS</span>
            </div>
            <table className="lb-table">
              <thead>
                <tr>
                  <th>Rank</th><th>Recruiter</th>
                  <th>Candidates<br />Contacted</th><th>Offers</th>
                  <th>Success<br />Rate</th>
                </tr>
              </thead>
              <tbody id="leaderboard-body"><Leaderboard entries={lb} /></tbody>
            </table>
          </section>
        </div>
      </div>

      <section className="card positions-section">
        <div className="card-head">
          <h2>📌 Open Positions — Pipeline Progress</h2>
          <span className="card-sub" id="positions-sub">
            INTERVIEW TARGET = {interviewTarget}/WEEK · TOTALS{" "}
            {shown.from ? fmtDate(shown.from) : "—"} → {shown.to ? fmtDate(shown.to) : "—"}
          </span>
        </div>
        <div className="pos-stats" id="pos-stats">
          <PositionStats snaps={snaps} target={interviewTarget} />
        </div>
        <div className="pos-grid" id="pos-grid"><Positions snaps={snaps} /></div>
      </section>

      <section className="card rto-section" id="offer-pipeline">
        <RtoBenchmark
          min={rtoMin} max={rtoMax} phase={rtoPhase}
          onViewAll={() => setModal("rto-directory")}
          onChangeStatus={changeRtoStatus}
        />
      </section>

      <section className="card kpi-benchmark">
        <div className="card-head">
          <h2>🎯 Monthly KPI Benchmark</h2>
          <div className="month-nav">
            <button id="month-prev" className="btn-icon" onClick={() => stepMonth(-1)}>‹</button>
            <span id="kpi-month-label">{month ? monthLabel(month) : "—"}</span>
            <button id="month-next" className="btn-icon" onClick={() => stepMonth(1)}>›</button>
          </div>
        </div>
        <p className="kpi-note">
          Targets per recruiter per month: <b>40 interviews</b> (1,000,000 ₫) ·{" "}
          <b>8 ready-to-offer</b> (1,000,000 ₫) · both = <b>top-up</b> (500,000 ₫) ·{" "}
          plus hire achievements logged in "TA KPI Achievements".
        </p>
        <div className="kpi-legend" id="kpi-legend"><KpiLegend /></div>
        <div id="kpi-rows">
          <KpiBenchmark rows={kpiRows} monthLabelText={month ? monthLabel(month) : null} />
        </div>
      </section>

      {modal === "daily" && (
        <LogDailyModal
          api={entry.api} isDemo={entry.isDemo}
          onClose={() => setModal(null)} onSaved={refreshData}
        />
      )}
      {modal === "rto" && (
        <LogRtoModal
          api={entry.api} isDemo={entry.isDemo}
          onClose={() => setModal(null)} onSaved={refreshRto}
        />
      )}
      {modal === "kpi" && (
        <LogKpiModal
          api={entry.api} isDemo={entry.isDemo} account={entry.account}
          onClose={() => setModal(null)} onSaved={refreshData}
        />
      )}
      {modal === "rto-directory" && (
        <RtoDirectoryModal items={rtoItems || []} onClose={() => setModal(null)} />
      )}

      <footer className="footer">
        Data: SharePoint · ORG HR Admin — “Recruitment Funnel Weekly” + “TA KPI Achievements”.
        Recruiter attribution = row author. Outcome distribution is estimated from funnel drop-offs.
      </footer>
    </main>
  );
}
